import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FlexControlledForm,
  HealthPlanStatuses,
  Inscription,
  Preloader,
  ServiceConfig,
  WarningLabel,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { PendingModal } from '@/components/elements';
import useIsEmbeddedOnly from '@/modules/deductibles/hooks/useIsEmbeddedOnly.ts';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores';
import { useStore as useMatrixStore } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/stores/useEnrollmentMatrix.store.ts';
import { useProcessingRuleTiersStore } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/stores/useProcessingRuleTiersStore';
import { useDeleteService } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/queries';
import { NEW_PROCESSING_RULE_KEY } from '@/modules/HealthPlan/HealthPlanSetup/HealthPlanSetup.constants';
import theme from '@/styles/theme';

import { useSubmitFormStore } from '../../stores';

import { HEALTH_PLAN_KEY, MANUAL_CLAIM_KEY } from './hooks/useEnrollmentMatrixFields';
import { useProcessingRuleModal } from './hooks/useProcessingRuleModal';
import ProcessingRuleModalV2 from './ProcessingRuleModalV2/ProcessingRuleModal';
import { EnrollmentMatrixPayload, useStore } from './stores/useEnrollmentMatrix.store';
import { useTiers } from './Tiers/hooks';
import { TierItem } from './Tiers/TierItem';
import { useEnrollmentMatrixFields, useProcessingRulesData } from './hooks';

interface Props {
  isEditMode?: boolean;
  isTemplateMode?: boolean;
  serviceConfigs: ServiceConfig[];
  onDirty?: (isDirty: boolean) => void;
}
const EnrollmentMatrix: React.FC<Props> = ({ isEditMode, isTemplateMode, serviceConfigs, onDirty }) => {
  const saveValidate = useSubmitFormStore((state) => state.saveValidate);
  const activateValidate = useSubmitFormStore((state) => state.activateValidate);
  const currentState = useStore((state) => state.state);
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const healthPlanStatus = useHealthPlanNameStore((state) => state.state.healthPlanStatus);
  const parentTemplate = useHealthPlanNameStore((state) => state.state.parentTemplate);
  const processingRuleSourceId = useMatrixStore((state) => state.sourceState?.processingRuleName);

  const [deductibleWarnModalVisible, setDeductibleWarnModalVisible] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { handleDelete, isLoading } = useDeleteService();

  const handleSetState = useStore((state) => state.setState);

  const {
    currentRule,
    isLoadingCurrentRule,
    processingRulesList,
    isLoadingRules,
    isFieldLoading,
    handleLoadMoreRules,
    resetPagination,
  } = useProcessingRulesData({
    currentProcessingRuleName: currentState.processingRuleName,
    processingRuleSourceId,
    parentTemplate,
  });

  const resetTiers = useProcessingRuleTiersStore((state) => state.resetTiers);

  const {
    isModalVisible: processingRuleModalVisible,
    editingRuleId,
    openModalForCreate,
    openModalForEdit,
    handleModalClose,
    handleRuleCreated: handleRuleCreatedBase,
  } = useProcessingRuleModal({
    currentProcessingRuleName: currentState?.processingRuleName,
    processingRuleSourceId,
    onStateChange: handleSetState,
    onFormKeyChange: () => setFormKey((prev) => prev + 1),
    onResetPagination: resetPagination,
  });

  const handleRuleCreated = useCallback(
    (ruleId?: string) => {
      resetTiers();
      handleRuleCreatedBase(ruleId);
    },
    [resetTiers, handleRuleCreatedBase]
  );

  const fields = useEnrollmentMatrixFields(
    activateValidate,
    isTemplateMode,
    isEditMode,
    processingRulesList,
    healthPlanStatus === HealthPlanStatuses.ACTIVE || healthPlanStatus === HealthPlanStatuses.PENDING_ACTIVE,
    parentTemplate,
    openModalForEdit,
    isLoadingRules,
    handleLoadMoreRules,
    processingRuleSourceId,
    isFieldLoading
  );
  const {
    fieldsValues: tiersValues,
    getFieldValuesById,
    getServiceConfigsByTierId,
  } = useTiers(currentRule, serviceConfigs, isLoadingCurrentRule);

  const isEmbeddedOnly = useIsEmbeddedOnly((currentRule?.tiers ?? []).map((tier) => tier.tierTriggers || []));

  const showErrors = useStore((state) => state.showErrors);
  const handleChangeValues = useCallback(
    (values: Partial<EnrollmentMatrixPayload>) => {
      if (values?.processingRuleName === NEW_PROCESSING_RULE_KEY) {
        openModalForCreate();
        return;
      }

      // Reset payout rules when processing rule changes
      if (values?.processingRuleName && values.processingRuleName !== currentState.processingRuleName) {
        resetTiers();
      }

      handleSetState(values);
      if (
        !!serviceConfigs?.length &&
        !isTemplateMode &&
        values?.deductibleService &&
        currentState.deductibleService &&
        currentState.deductibleService !== values?.deductibleService
      ) {
        setDeductibleWarnModalVisible(true);
      }
    },
    [
      serviceConfigs?.length,
      currentState.deductibleService,
      currentState.processingRuleName,
      isTemplateMode,
      handleSetState,
      openModalForCreate,
      resetTiers,
    ]
  );

  const handleDeleteAllConfigs = useCallback(async () => {
    try {
      await Promise.all(
        serviceConfigs?.map(async (item) => {
          await handleDelete(item?.id);
        })
      );
    } finally {
      setDeductibleWarnModalVisible(false);
    }
  }, [serviceConfigs, handleDelete]);
  const key = useMemo(
    () => `${_.get(fields, '[2].defaultValue', '')}-${formKey}`,
    [fields, formKey]
  ) as string;
  return (
    <>
      <Box background="canvas" round="container1Round">
        <FlexControlledForm
          key={key}
          testId="enrollment-matrix-definition"
          fields={fields}
          showError={showErrors || saveValidate || activateValidate}
          editMode={isEditMode}
          onChangeValues={handleChangeValues}
          onDirty={onDirty}
          isThinMode
          footer={
            isEditMode &&
            isTemplateMode && (
              <Box margin={{ top: 'spacing16' }} pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}>
                <Box
                  pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
                  background={{ color: 'warningContainer' }}
                  border={{ size: '1px', color: 'warningBorder' }}
                  round="fieldRound"
                >
                  <Inscription color="textBody">
                    <WarningLabel>Changing the rule resets the tiers below.</WarningLabel>
                  </Inscription>
                </Box>
              </Box>
            )
          }
        />
      </Box>
      {!!tiersValues?.length && (
        <Box
          background="canvas"
          round="container1Round"
          pad="l"
          margin={{ top: 'l' }}
          border={{ color: 'border2', size: 'small' }}
        >
          {tiersValues.map((item, index) => (
            <TierItem
              id={item.id}
              isRemoveMode={tiersValues.length > 1}
              index={index}
              isLastItem={_.last(tiersValues) === item}
              key={item.id}
              defaultValues={getFieldValuesById(item.id)}
              isTemplateMode={isTemplateMode}
              isEditMode={isEditMode}
              payoutDefinitionId={item.payoutDefinition}
              serviceConfigs={getServiceConfigsByTierId(item.id)}
              deductibleType={currentState?.deductibleService}
              healthPlanTypes={healthPlanTypes}
              healthPlanStatus={healthPlanStatus}
              isEmbeddedOnly={isEmbeddedOnly}
            />
          ))}
        </Box>
      )}
      {!!serviceConfigs?.length && (
        <PendingModal
          visible={deductibleWarnModalVisible}
          warningIcon
          onSetVisible={() => {
            handleSetState({
              ...currentState,
              deductibleService:
                currentState?.deductibleService === HEALTH_PLAN_KEY ? MANUAL_CLAIM_KEY : HEALTH_PLAN_KEY,
            });
            setDeductibleWarnModalVisible(false);
          }}
          header={`Update Deductible Tracking to ${currentState?.deductibleService === MANUAL_CLAIM_KEY ? 'Manual Claims' : 'Carrier Claims'}?`}
          helptext="By updating the type of claim used for tracking deductibles, any previously entered Service Payment rules will be removed from the tiers. Once updated, verify each tier to setup the new Service Payment rules."
        >
          <Box direction="row">
            <Box margin={{ right: 'spacing12' }}>
              <AppButton
                onClick={() => {
                  handleSetState({
                    ...currentState,
                    deductibleService:
                      currentState?.deductibleService === HEALTH_PLAN_KEY
                        ? MANUAL_CLAIM_KEY
                        : HEALTH_PLAN_KEY,
                  });
                  setDeductibleWarnModalVisible(false);
                }}
                width="140px"
                buttonType="secondary"
              >
                Cancel
              </AppButton>
            </Box>
            <AppButton
              disabled={isLoading}
              onClick={() => {
                handleDeleteAllConfigs();
              }}
              width="133px"
              style={{ background: isLoading ? theme.colors.border2 : theme.colors.danger }}
            >
              {isLoading ? <Preloader color="white" /> : 'Yes, Change'}
            </AppButton>
          </Box>
        </PendingModal>
      )}

      {processingRuleModalVisible && (
        <ProcessingRuleModalV2
          updatedElement={editingRuleId ? currentRule : undefined}
          visible={processingRuleModalVisible}
          onToggleVision={(visible) => {
            handleModalClose(visible);
          }}
          onUpdateElement={handleRuleCreated}
          isTemplateMode={isTemplateMode}
        />
      )}
    </>
  );
};
export default EnrollmentMatrix;
